import { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { Key, Loader2, Copy, Check, ExternalLink, AlertTriangle } from 'lucide-react'
import { useAppDispatch } from '@/redux/hooks'
import { getRoomDetails, getRoomMembers } from '@/redux/asyncThunks/room'
import { RootState } from '@/redux/store'
import { selectRoomPlayers } from '@/redux/selectors/roomSelector'
import { Status } from '@/redux/types/room'
import { THIRD_PARTY_SWAGGER_URL } from '@/api/config'
import {
  createThirdPartyKey,
  listThirdPartyKeys,
  revokeThirdPartyKey,
  ThirdPartyApiKeyCreated,
  ThirdPartyApiKeyListItem,
} from '@/api/thirdPartyApiKeys'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { cn } from '@/lib/utils'
import { formatDate } from '@/utils/dateTimeUtils'
import { toast } from 'sonner'
import RoomNotFound from '@/components/PageNotFound/RoomNotFound'

function SecretCopyRow({
  label,
  value,
}: {
  label: string
  value: string
}) {
  const [copied, setCopied] = useState<boolean>(false)
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value)
      setCopied(true)
      toast.success('Copied')
      setTimeout(() => setCopied(false), 1500)
    } catch {
      toast.error('Could not copy')
    }
  }
  return (
    <div className='space-y-1'>
      <Label className='text-xs text-muted-foreground'>{label}</Label>
      <div className='flex gap-2 items-start'>
        <code className='flex-1 break-all rounded-md bg-muted px-3 py-2 text-sm'>{value}</code>
        <Button type='button' variant='outline' size='icon' onClick={copy} aria-label={`Copy ${label}`}>
          {copied ? <Check className='h-4 w-4' /> : <Copy className='h-4 w-4' />}
        </Button>
      </div>
    </div>
  )
}

function keyStatusLabel(key: ThirdPartyApiKeyListItem): string {
  if (!key.is_active) return 'Revoked'
  if (key.is_expired) return 'Expired'
  return 'Active'
}

const ThirdPartyApiAccessScreen: React.FC = () => {
  const { roomId } = useParams<{ roomId: string }>()
  const dispatch = useAppDispatch()
  const { user } = useSelector((state: RootState) => state.user)
  const { currentRoom, isLoading, error } = useSelector((state: RootState) => state.room)
  const players = useSelector(selectRoomPlayers)

  const [keys, setKeys] = useState<ThirdPartyApiKeyListItem[]>([])
  const [keysLoading, setKeysLoading] = useState<boolean>(false)
  const [keysError, setKeysError] = useState<string | null>(null)

  const [label, setLabel] = useState<string>('')
  const [playerId, setPlayerId] = useState<string>('')
  const [expiresLocal, setExpiresLocal] = useState<string>('')
  const [creating, setCreating] = useState<boolean>(false)
  const [createFieldError, setCreateFieldError] = useState<string | null>(null)

  const [createdSecret, setCreatedSecret] = useState<ThirdPartyApiKeyCreated | null>(null)
  const [revokeTarget, setRevokeTarget] = useState<ThirdPartyApiKeyListItem | null>(null)
  const [revoking, setRevoking] = useState<boolean>(false)

  const [roomIdCopied, setRoomIdCopied] = useState<boolean>(false)

  const isCurrentRoomLoaded = currentRoom?.roomid === roomId
  const isOwner =
    Boolean(user?.email && currentRoom?.owner && currentRoom.owner === user.email)

  const acceptedPlayers = useMemo(
    () =>
      players.filter(
        (m) =>
          m.invitation_status === Status.Accepted &&
          m.member_id != null &&
          String(m.member_id).length > 0
      ),
    [players]
  )

  const loadKeys = useCallback(async () => {
    if (!roomId || !isOwner) return
    setKeysLoading(true)
    setKeysError(null)
    try {
      const data = await listThirdPartyKeys(roomId)
      setKeys(data)
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to load API keys'
      setKeysError(msg)
    } finally {
      setKeysLoading(false)
    }
  }, [roomId, isOwner])

  useEffect(() => {
    if (roomId) {
      dispatch(getRoomMembers(roomId))
      dispatch(getRoomDetails(roomId))
    }
  }, [dispatch, roomId])

  useEffect(() => {
    if (isOwner && isCurrentRoomLoaded) {
      void loadKeys()
    }
  }, [isOwner, isCurrentRoomLoaded, loadKeys])

  const copyRoomId = async () => {
    if (!roomId) return
    try {
      await navigator.clipboard.writeText(roomId)
      setRoomIdCopied(true)
      toast.success('Room ID copied')
      setTimeout(() => setRoomIdCopied(false), 1500)
    } catch {
      toast.error('Could not copy')
    }
  }

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    setCreateFieldError(null)
    if (!roomId || !isOwner) return
    const pid = Number(playerId)
    if (!playerId || Number.isNaN(pid)) {
      setCreateFieldError('Select a player.')
      return
    }
    const trimmedLabel = label.trim()
    if (!trimmedLabel) {
      setCreateFieldError('Enter a label for this key.')
      return
    }
    let expires_at: string | undefined
    if (expiresLocal) {
      const d = new Date(expiresLocal)
      if (Number.isNaN(d.getTime())) {
        setCreateFieldError('Invalid expiry date.')
        return
      }
      expires_at = d.toISOString()
    }
    setCreating(true)
    try {
      const created = await createThirdPartyKey(roomId, {
        player_id: pid,
        label: trimmedLabel,
        expires_at: expires_at ?? null,
      })
      setCreatedSecret(created)
      setLabel('')
      setPlayerId('')
      setExpiresLocal('')
      toast.success('API key created')
      await loadKeys()
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Could not create key'
      setCreateFieldError(msg)
      toast.error(msg)
    } finally {
      setCreating(false)
    }
  }

  const handleRevoke = async () => {
    if (!roomId || !revokeTarget) return
    setRevoking(true)
    try {
      await revokeThirdPartyKey(roomId, revokeTarget.key_prefix)
      toast.success('API key revoked')
      setRevokeTarget(null)
      await loadKeys()
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Could not revoke key'
      toast.error(msg)
    } finally {
      setRevoking(false)
    }
  }

  if (!roomId) {
    return (
      <div className='p-6 text-red-700 rounded-md bg-red-50'>
        <p>Error: Room ID is required</p>
      </div>
    )
  }

  if (isLoading && !currentRoom) {
    return (
      <div className='min-h-screen flex items-center justify-center'>
        <Loader2 className='h-10 w-10 animate-spin text-dark-blue' aria-hidden />
      </div>
    )
  }

  if (error || !currentRoom) {
    return (
      <div className='container px-4 py-8 mx-auto'>
        <RoomNotFound message='Unable to fetch the room.' />
      </div>
    )
  }

  return (
    <div className='min-h-screen'>
      <div className='container px-4 py-8 mx-auto max-w-5xl'>
        <Card className='bg-white shadow-md mb-6'>
          <CardHeader>
            <div className='flex items-start gap-3'>
              <div className='rounded-lg bg-dark-blue/10 p-2'>
                <Key className='h-7 w-7 text-dark-blue' aria-hidden />
              </div>
              <div>
                <CardTitle className='text-2xl font-bold text-dark-blue'>
                  Third-party API access
                </CardTitle>
                <CardDescription className='text-base mt-1'>
                  Issue and manage API keys for external integrations. Keys authenticate as a specific
                  player in this room.
                </CardDescription>
              </div>
            </div>
          </CardHeader>
        </Card>

        {!isOwner ? (
          <Card className='border-amber-200 bg-amber-50'>
            <CardContent className='pt-6 flex gap-3'>
              <AlertTriangle className='h-5 w-5 text-amber-700 flex-shrink-0 mt-0.5' />
              <div>
                <p className='font-semibold text-amber-900'>Room owner only</p>
                <p className='text-sm text-amber-900/90 mt-1'>
                  Only the game owner can create or revoke third-party API keys. If you need access,
                  ask the professor who created this room.
                </p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <>
            <Card className='mb-6'>
              <CardHeader>
                <CardTitle className='text-lg text-dark-blue'>Connection details</CardTitle>
                <CardDescription>
                  Copy the Room ID for API requests; open Swagger for endpoint documentation. While you
                  use this app, your login session sends authentication automatically — you do not need
                  to copy a token from the browser for actions on this page.
                </CardDescription>
              </CardHeader>
              <CardContent className='space-y-4'>
                <div>
                  <Label className='text-xs uppercase tracking-wide text-muted-foreground'>
                    Room ID
                  </Label>
                  <div className='mt-1 flex flex-wrap items-center gap-2'>
                    <code className='flex-1 min-w-0 break-all rounded-md border bg-muted px-3 py-2 text-sm font-mono'>
                      {roomId}
                    </code>
                    <Button
                      type='button'
                      variant='outline'
                      size='sm'
                      onClick={copyRoomId}
                      className='flex-shrink-0'
                    >
                      {roomIdCopied ? (
                        <Check className='h-4 w-4' />
                      ) : (
                        <Copy className='h-4 w-4' />
                      )}
                      <span className='ml-2'>Copy</span>
                    </Button>
                  </div>
                </div>
                <div className='pt-2'>
                  <a
                    href={THIRD_PARTY_SWAGGER_URL}
                    target='_blank'
                    rel='noopener noreferrer'
                    className='inline-flex items-center gap-2 text-sm font-medium text-blue-600 hover:underline'
                  >
                    Third Party API documentation (Swagger)
                    <ExternalLink className='h-4 w-4' aria-hidden />
                  </a>
                </div>
              </CardContent>
            </Card>

            <Card className='mb-6'>
              <CardHeader>
                <CardTitle className='text-lg text-dark-blue'>Issue a new key</CardTitle>
                <CardDescription>
                  Choose an <strong>accepted</strong> player, add a label, and optionally set an
                  expiry. The secret key is shown only once after creation.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleCreate} className='space-y-4 max-w-lg'>
                  <div className='space-y-2'>
                    <Label htmlFor='tp-player'>Player</Label>
                    <Select value={playerId} onValueChange={setPlayerId}>
                      <SelectTrigger id='tp-player'>
                        <SelectValue placeholder='Select a player' />
                      </SelectTrigger>
                      <SelectContent>
                        {acceptedPlayers.length === 0 ? (
                          <div className='px-2 py-4 text-sm text-muted-foreground'>
                            No accepted players in this room yet.
                          </div>
                        ) : (
                          acceptedPlayers.map((m) => {
                            const id = String(m.member_id)
                            const label = m.member_name?.trim()
                              ? `${m.member_name} (${m.member_email})`
                              : m.member_email || id
                            return (
                              <SelectItem key={id} value={id}>
                                {label}
                              </SelectItem>
                            )
                          })
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className='space-y-2'>
                    <Label htmlFor='tp-label'>Label</Label>
                    <Input
                      id='tp-label'
                      value={label}
                      onChange={(e) => setLabel(e.target.value)}
                      placeholder='e.g. LMS integration, Research bot'
                      autoComplete='off'
                    />
                  </div>
                  <div className='space-y-2'>
                    <Label htmlFor='tp-expires'>Expires (optional)</Label>
                    <Input
                      id='tp-expires'
                      type='datetime-local'
                      value={expiresLocal}
                      onChange={(e) => setExpiresLocal(e.target.value)}
                    />
                    <p className='text-xs text-muted-foreground'>
                      If left blank, the server applies its default expiry for new keys.
                    </p>
                  </div>
                  {createFieldError && (
                    <p className='text-sm text-destructive' role='alert'>
                      {createFieldError}
                    </p>
                  )}
                  <Button
                    type='submit'
                    disabled={creating || acceptedPlayers.length === 0}
                    className='bg-dark-blue hover:bg-dark-blue-hovered'
                  >
                    {creating ? (
                      <>
                        <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                        Creating…
                      </>
                    ) : (
                      'Generate API key'
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className='text-lg text-dark-blue'>Existing keys</CardTitle>
                <CardDescription>
                  Only the key prefix is stored for reference. Revoke a key if it may be
                  compromised; you can create a new key after revoking (or after expiry).
                </CardDescription>
              </CardHeader>
              <CardContent>
                {keysLoading ? (
                  <div className='flex justify-center py-12'>
                    <Loader2 className='h-8 w-8 animate-spin text-dark-blue' />
                  </div>
                ) : keysError ? (
                  <p className='text-sm text-destructive'>{keysError}</p>
                ) : keys.length === 0 ? (
                  <p className='text-sm text-muted-foreground py-4'>No keys issued yet.</p>
                ) : (
                  <div className='overflow-x-auto rounded-md border'>
                    <table className='w-full text-sm'>
                      <thead>
                        <tr className='border-b bg-muted/50 text-left'>
                          <th className='p-3 font-semibold'>Prefix</th>
                          <th className='p-3 font-semibold'>Label</th>
                          <th className='p-3 font-semibold'>Player</th>
                          <th className='p-3 font-semibold'>Status</th>
                          <th className='p-3 font-semibold'>Expires</th>
                          <th className='p-3 font-semibold'>Last used</th>
                          <th className='p-3 font-semibold w-[100px]'>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {keys.map((k) => (
                          <tr key={k.id} className='border-b last:border-0'>
                            <td className='p-3 font-mono'>{k.key_prefix}…</td>
                            <td className='p-3'>{k.label}</td>
                            <td className='p-3'>
                              <div>{k.player_name || '—'}</div>
                              <div className='text-xs text-muted-foreground'>{k.player_email}</div>
                            </td>
                            <td className='p-3'>
                              <span
                                className={cn(
                                  'inline-flex rounded-full px-2 py-0.5 text-xs font-medium',
                                  keyStatusLabel(k) === 'Active' &&
                                    'bg-green-100 text-green-800',
                                  keyStatusLabel(k) === 'Expired' &&
                                    'bg-amber-100 text-amber-900',
                                  keyStatusLabel(k) === 'Revoked' &&
                                    'bg-muted text-muted-foreground'
                                )}
                              >
                                {keyStatusLabel(k)}
                              </span>
                            </td>
                            <td className='p-3 whitespace-nowrap'>
                              {k.expires_at ? formatDate(k.expires_at) : '—'}
                            </td>
                            <td className='p-3 whitespace-nowrap'>
                              {k.last_used_at ? formatDate(k.last_used_at) : '—'}
                            </td>
                            <td className='p-3'>
                              {k.is_active && !k.is_expired ? (
                                <Button
                                  type='button'
                                  variant='outline'
                                  size='sm'
                                  className='text-destructive border-destructive/40 hover:bg-destructive/10'
                                  onClick={() => setRevokeTarget(k)}
                                >
                                  Revoke
                                </Button>
                              ) : (
                                <span className='text-xs text-muted-foreground'>—</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>

      <Dialog open={Boolean(createdSecret)} onOpenChange={(open) => !open && setCreatedSecret(null)}>
        <DialogContent className='max-w-lg'>
          <DialogHeader>
            <DialogTitle>Save your API key</DialogTitle>
            <DialogDescription>
              This secret is shown only once. Copy it now and store it securely. You cannot retrieve
              it again from this app.
            </DialogDescription>
          </DialogHeader>
          {createdSecret && (
            <div className='space-y-4 py-2'>
              <SecretCopyRow label='API key' value={createdSecret.raw_key} />
              <SecretCopyRow label='Key prefix' value={createdSecret.key_prefix} />
            </div>
          )}
          <DialogFooter>
            <Button type='button' onClick={() => setCreatedSecret(null)}>
              I have saved it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(revokeTarget)} onOpenChange={(open) => !open && setRevokeTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Revoke API key?</DialogTitle>
            <DialogDescription>
              This immediately disables the key ending in{' '}
              <strong className='font-mono'>{revokeTarget?.key_prefix}</strong>. Applications using it
              will stop working until you issue a new key.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className='gap-2 sm:gap-0'>
            <Button type='button' variant='outline' onClick={() => setRevokeTarget(null)}>
              Cancel
            </Button>
            <Button
              type='button'
              variant='destructive'
              disabled={revoking}
              onClick={() => void handleRevoke()}
            >
              {revoking ? <Loader2 className='h-4 w-4 animate-spin' /> : 'Revoke key'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default ThirdPartyApiAccessScreen
